import React, { useState, useEffect, useMemo } from "react";
import DataTable from "components/DataTable";
import PageWrapper from "components/PageWrapper";
import ShowToast from "components/ShowToast";
import { fetchWrapper } from "utils/apiFetch";
import { useTableData } from "../../hooks/useTableData";
import { walletSettlementReportsConfig } from "config/tableConfigs";
import { useSession } from "hooks/useSession";
import ManualPayoutStatusModal from "./MannualPayoutStatusModal";
import PayoutReceiptModal from "./PayoutReceipt";

const WalletSettlementReportsPage = () => {
  const { userid: sessionUserId, role: sessionRole } = useSession();
  const currentUserId = sessionUserId || localStorage.getItem("userid") || "";
  const currentRole = sessionRole || localStorage.getItem("role") || "API Partner";
  const userRole = currentRole === "Platform User" ? "Admin" : currentRole;
  const isAdmin =
    userRole === "Admin" ||
    (currentRole || "").toLowerCase().includes("admin") ||
    (currentRole || "").toLowerCase() === "white label";

  const [showReceipt, setShowReceipt] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [checkingStatusId, setCheckingStatusId] = useState(null);

  const getTodayRange = () => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    return {
      start: todayStr,
      end: todayStr,
    };
  };

  const { start, end } = getTodayRange();

  const [apiUrl, setApiUrl] = useState(
    `https://pro.mokshiq.in/get_payout_report?user=${currentUserId}&filter_date=${start}&end_date=${end}`
  );

  useEffect(() => {
    if (currentUserId) {
      setApiUrl((prev) => {
        try {
          const url = new URL(prev);
          url.searchParams.set("user", currentUserId);
          return url.toString();
        } catch {
          return `https://pro.mokshiq.in/get_payout_report?user=${currentUserId}&filter_date=${start}&end_date=${end}`;
        }
      });
    }
  }, [currentUserId, start, end]);

  const tableData = useTableData(apiUrl, 0, walletSettlementReportsConfig);

  const refreshTable = () => {
    // cache-bust to force refetch
    setApiUrl((prev) => prev.split("&_r=")[0] + "&_r=" + Date.now());
  };

  const handleUpdated = () => {
    refreshTable();
  };

  // ── Cyrus payout: "check status"
  const handleCyrusCheckStatus = async (row) => {
    const orderId = row.ref_no || row.ref_id || row.orderId || row.ack_no;
    if (!orderId) {
      ShowToast("This row has no orderId/reference to check", "warning");
      return;
    }

    setCheckingStatusId(row.id);
    try {
      const formData = new FormData();
      formData.append("orderId", orderId);

      const res = await fetchWrapper("https://pro.mokshiq.in/billpay_check_status", {
        method: "POST",
        body: formData,
      });

      if (res?.status) {
        ShowToast(`Status: ${res.message || "Checked"}`, "success");
      } else {
        ShowToast(res?.message || "Status check failed", "danger");
      }
      refreshTable();
    } catch (e) {
      ShowToast("Something went wrong while checking status", "danger");
    } finally {
      setCheckingStatusId(null);
    }
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setShowReceipt(true);
  };

  const handleDateFilter = (s, e) => {
    setApiUrl(
      `https://pro.mokshiq.in/get_payout_report?user=${currentUserId}&filter_date=${s}&end_date=${e}`
    );
  };

  // 🔥 ROLE → COLUMN BINDING ALIGNED WITH API RESPONSE
  const filteredConfig = useMemo(() => {
    const base = { ...walletSettlementReportsConfig };

    const standardPayoutCols = [
      "id",
      "ref_no",
      "ack_no",
      "created_at",
      "product",
      "account_number",
      "holder_name",
      "number",
      "amount",
      "status",
      "Opening Balance",
      "Commission",
      "TDS",
      "Charge",
      "GST",
      "Closing Balance",
      "bank_rrn",
    ];

    const roleMap = {
      "API Partner": standardPayoutCols,
      "Api Partner": standardPayoutCols,
      Retailer: standardPayoutCols,
      Distributer: standardPayoutCols,
      "Super Distributer": standardPayoutCols,
      "White label": standardPayoutCols,
      Admin: standardPayoutCols,
      "Main Admin": standardPayoutCols,
    };

    const allowedKeys = roleMap[userRole] || roleMap[currentRole] || standardPayoutCols;
    base.columns = base.columns.filter((c) => allowedKeys.includes(c.key));

    return base;
  }, [userRole, currentRole]);

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedRow(null);
  };

  return (
    <div>
      {(loading || checkingStatusId) && (
        <div className="page-loader-overlay d-flex justify-content-center align-items-center">
          <div className="spinner-border text-primary" />
        </div>
      )}

      <PageWrapper config={walletSettlementReportsConfig} onDateFilter={handleDateFilter}>
        <DataTable
          config={filteredConfig}
          tableData={tableData}
          onView={handleView}
          // onStatusCheck={{}}
          onEdit={() => { }}  
          onDelete={() => { }}
          onExport={() => { }}
          onDeleteSelected={() => { }}
        />
      </PageWrapper>

      {isAdmin && (
        <ManualPayoutStatusModal
          show={showModal}
          transaction={selectedTxn}
          onClose={() => setShowModal(false)}
          onUpdated={handleUpdated}
        />
      )}

      <PayoutReceiptModal
        isOpen={showReceipt}
        onClose={handleCloseReceipt}
        receiptData={selectedRow}
      />
    </div>
  );
};

export default WalletSettlementReportsPage;